import React from "react";
import PageHero from "../components/PageHero";
import {
  Factory,
  ShieldCheck,
  FlaskConical,
  Settings2,
  Truck,
  BadgeCheck,
  Recycle,
  CheckCircle2,
} from "lucide-react";

export default function QualitySafety() {
  const qualityProtocols = [
    "Compliance",
    "Transparency",
    "Consistency",
    "Continuous Improvement",
    "Accountability",
  ];

  const testing = [
    "Slump Testing",
    "Cube Testing",
    "Continuous Batch Monitoring",
  ];

  const sustainability = [
    "Efficient water recycling",
    "Supplementary cementitious materials",
    "Energy efficient operations",
    "Responsible aggregate sourcing",
  ];

  const sections = [
    {
      icon: Factory,
      title: "Computerized Batching and Process Control",
      description:
        "Our RMC plants are equipped with fully automated computerized batching systems ensuring accurate proportioning of cement, aggregates, water, and admixtures.",
    },
    {
      icon: ShieldCheck,
      title: "Quality Control and Testing Protocols",
      description:
        "We follow a structured Quality Control system based on compliance, transparency, consistency, continuous improvement, and accountability.",
    },
    {
      icon: FlaskConical,
      title: "Raw Material Quality Assurance",
      description:
        "We maintain strict control over aggregates, cement quality, and admixture compatibility through in-house sourcing and vendor validation.",
    },
    {
      icon: Settings2,
      title: "Customized Mix Design Solutions",
      description:
        "Our technical team develops mixes based on IS codes, exposure class, workability, pumpability, and durability parameters.",
    },
    {
      icon: Truck,
      title: "Transit Mixer Fleet and GPS Tracking",
      description:
        "Our fleet is equipped with GPS enabled tracking and real-time dispatch monitoring ensuring timely delivery.",
    },
    {
      icon: BadgeCheck,
      title: "On Site Performance and Placement Support",
      description:
        "We ensure concrete is delivered under controlled conditions maintaining slump and structural performance.",
    },
  ];

  return (
    <main className="bg-white overflow-hidden">
      {/* HERO */}
      <PageHero
        title="Quality & Safety"
        subtitle="Engineered Precision. Assured Performance."
        num="03"
      />

      {/* INTRO */}
      <section className="relative py-20 md:py-24 bg-[#fafafa] overflow-hidden">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-[#ef2435]/5 blur-3xl" />

        <div className="relative max-w-[1350px] mx-auto px-5 md:px-8">

          <div className="max-w-4xl">

            <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full border border-[#ececef] bg-white mb-8">
              <div className="w-2 h-2 rounded-full bg-[#ef2435]" />

              <span className="text-[11px] uppercase tracking-[0.3em] font-semibold text-[#ef2435]">
                QUALITY & SAFETY
              </span>
            </div>

            <h2 className="text-4xl md:text-6xl font-black leading-[0.95] text-gray-900 mb-8">
              ENGINEERED
              <br />

              <span className="text-[#ef2435]">
                PRECISION.
              </span>
            </h2>

            <p className="text-gray-600 text-lg leading-[2]">
              At SSC READYMIX, quality is not merely an outcome.
              It is an integrated process embedded across batching,
              production, transit, and placement.
            </p>

          </div>
        </div>
      </section>

      {/* MAIN CARDS */}
      <section className="py-20 bg-white">
        <div className="max-w-[1350px] mx-auto px-5 md:px-8">

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">

            {sections.map((item, i) => (
              <div
                key={i}
                className="group relative rounded-[28px] border border-[#ececef] bg-[#fafafa] p-7 overflow-hidden hover:border-[#ef2435] hover:-translate-y-2 hover:shadow-[0_20px_60px_rgba(239,36,53,0.08)] transition-all duration-500"
              >

                <div className="absolute -right-12 -top-12 w-32 h-32 rounded-full bg-[#ef2435]/5 group-hover:bg-[#ef2435]/10 transition-all duration-500" />

                <div className="relative">

                  <div className="w-14 h-14 rounded-2xl bg-[#ef2435] flex items-center justify-center shadow-lg shadow-red-500/20 mb-6 group-hover:scale-110 transition-all duration-500">
                    <item.icon className="text-white" size={24} />
                  </div>

                  <h3 className="text-2xl font-black text-gray-900 leading-tight mb-5">
                    {item.title}
                  </h3>

                  <p className="text-gray-600 leading-8 text-[15px]">
                    {item.description}
                  </p>

                </div>
              </div>
            ))}

          </div>
        </div>
      </section>

      {/* QUALITY CONTROL */}
      <section className="py-20 bg-[#111] text-white relative overflow-hidden">

        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[700px] rounded-full bg-[#ef2435]/10 blur-3xl" />

        <div className="relative max-w-[1350px] mx-auto px-5 md:px-8">

          <div className="grid lg:grid-cols-2 gap-8">

            {/* LEFT */}
            <div className="rounded-[30px] border border-white/10 bg-white/5 backdrop-blur p-8 md:p-10">

              <span className="text-[#ef2435] text-xs uppercase tracking-[0.3em] font-semibold">
                QUALITY CONTROL
              </span>

              <h2 className="text-4xl md:text-5xl font-black leading-tight mt-5 mb-8">
                Testing Protocols
              </h2>

              <div className="space-y-5">

                {qualityProtocols.map((item, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-4"
                  >

                    <div className="w-10 h-10 rounded-full bg-[#ef2435]/10 border border-[#ef2435]/20 flex items-center justify-center">
                      <CheckCircle2
                        size={18}
                        className="text-[#ef2435]"
                      />
                    </div>

                    <span className="text-gray-200 text-lg">
                      {item}
                    </span>

                  </div>
                ))}

              </div>
            </div>

            {/* RIGHT */}
            <div className="rounded-[30px] border border-white/10 bg-white/5 backdrop-blur p-8 md:p-10">

              <span className="text-[#ef2435] text-xs uppercase tracking-[0.3em] font-semibold">
                REGULAR TESTING
              </span>

              <h2 className="text-4xl md:text-5xl font-black leading-tight mt-5 mb-8">
                Batch Monitoring
              </h2>

              <div className="space-y-5">

                {testing.map((item, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-4"
                  >

                    <div className="w-10 h-10 rounded-full bg-[#ef2435]/10 border border-[#ef2435]/20 flex items-center justify-center">
                      <CheckCircle2
                        size={18}
                        className="text-[#ef2435]"
                      />
                    </div>

                    <span className="text-gray-200 text-lg">
                      {item}
                    </span>

                  </div>
                ))}

              </div>
            </div>

          </div>
        </div>
      </section>

      {/* FINAL SECTION */}
      <section className="py-20 bg-[#fafafa] overflow-hidden relative">

        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] rounded-full bg-[#ef2435]/5 blur-3xl" />

        <div className="relative max-w-[1350px] mx-auto px-5 md:px-8">

          <div className="grid lg:grid-cols-2 gap-8">

            {/* COMPLIANCE */}
            <div className="group rounded-[30px] bg-white border border-[#ececef] p-8 md:p-10 hover:border-[#ef2435] hover:-translate-y-2 transition-all duration-500">

              <div className="w-16 h-16 rounded-2xl bg-[#ef2435] flex items-center justify-center mb-8 shadow-lg shadow-red-500/20">
                <ShieldCheck
                  className="text-white"
                  size={30}
                />
              </div>

              <h2 className="text-3xl md:text-4xl font-black leading-tight text-gray-900 mb-6">
                Compliance with Industry Standards
              </h2>

              <p className="text-gray-600 leading-[2] text-[16px]">
                All operations align with Indian Standards and
                industry best practices.
              </p>

            </div>

            {/* SUSTAINABILITY */}
            <div className="group rounded-[30px] bg-[#111] text-white p-8 md:p-10 relative overflow-hidden hover:-translate-y-2 transition-all duration-500">

              <div className="absolute bottom-0 right-0 w-72 h-72 rounded-full bg-[#ef2435]/10 blur-3xl" />

              <div className="relative">

                <div className="w-16 h-16 rounded-2xl bg-[#ef2435] flex items-center justify-center mb-8 shadow-lg shadow-red-500/20">
                  <Recycle
                    className="text-white"
                    size={30}
                  />
                </div>

                <h2 className="text-3xl md:text-4xl font-black leading-tight mb-8">
                  Sustainability &
                  Environmental Responsibility
                </h2>

                <div className="space-y-4">

                  {sustainability.map((item, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-4"
                    >

                      <div className="w-2.5 h-2.5 rounded-full bg-[#ef2435]" />

                      <span className="text-gray-200 text-[16px]">
                        {item}
                      </span>

                    </div>
                  ))}

                </div>

              </div>
            </div>

          </div>
        </div>
      </section>
    </main>
  );
}